import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import {
  DEFAULT_ENTRY_POINT,
  isEntryPointKey,
  type EntryPointKey,
  type Utm,
  type UtmKey,
} from "@/shared/config/referral";
import { getQueryParam } from "@/shared/lib/queryParams";
import { sanitizePartnerId, sanitizeChan } from "@/shared/lib/partnerParams";
import { sanitizeUtm } from "@/shared/lib/referralLink";

interface State {
  partnerId: string;
  /** affid пришёл из query-строки конструктора — поле не редактируется. */
  partnerIdLocked: boolean;
  chan: string;
  b: string;
  entryPoint: EntryPointKey;
  companies: string[];
  /** Пустой список СК означает «все», а снятый выбор — незаполненную настройку. */
  companiesCleared: boolean;
  hideHeader: boolean;
  hideFooter: boolean;
  noJivochat: boolean;
  utm: Utm;
}

type Action =
  | { type: "setPartnerId"; value: string }
  | { type: "setChan"; value: string }
  | { type: "setB"; value: string }
  | { type: "setEntryPoint"; value: EntryPointKey }
  | { type: "setCompanies"; value: string[]; cleared: boolean }
  | { type: "setHideHeader"; value: boolean }
  | { type: "setHideFooter"; value: boolean }
  | { type: "setNoJivochat"; value: boolean }
  | { type: "setUtm"; key: UtmKey; value: string };

function createInitialState(): State {
  const queryPartnerId = sanitizePartnerId(getQueryParam("affid"));
  const entryPoint = getQueryParam("entry");

  return {
    partnerId: queryPartnerId,
    partnerIdLocked: Boolean(queryPartnerId),
    chan: sanitizeChan(getQueryParam("chan")),
    b: sanitizePartnerId(getQueryParam("b")),
    entryPoint: isEntryPointKey(entryPoint) ? entryPoint : DEFAULT_ENTRY_POINT,
    companies: [],
    companiesCleared: false,
    hideHeader: false,
    hideFooter: false,
    noJivochat: false,
    utm: {},
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "setPartnerId":
      if (state.partnerIdLocked) return state;
      return { ...state, partnerId: sanitizePartnerId(action.value) };
    case "setChan":
      return { ...state, chan: sanitizeChan(action.value) };
    case "setB":
      return { ...state, b: sanitizePartnerId(action.value) };
    case "setEntryPoint":
      return { ...state, entryPoint: action.value };
    case "setCompanies":
      return { ...state, companies: action.value, companiesCleared: action.cleared };
    case "setHideHeader":
      return { ...state, hideHeader: action.value };
    case "setHideFooter":
      return { ...state, hideFooter: action.value };
    case "setNoJivochat":
      return { ...state, noJivochat: action.value };
    case "setUtm": {
      const value = sanitizeUtm(action.value);
      const utm = { ...state.utm };
      if (value) utm[action.key] = value;
      else delete utm[action.key];
      return { ...state, utm };
    }
    default:
      return state;
  }
}

interface ContextValue extends State {
  setPartnerId: (value: string) => void;
  setChan: (value: string) => void;
  setB: (value: string) => void;
  setEntryPoint: (value: EntryPointKey) => void;
  setCompanies: (codes: string[], cleared: boolean) => void;
  setHideHeader: (value: boolean) => void;
  setHideFooter: (value: boolean) => void;
  setNoJivochat: (value: boolean) => void;
  setUtm: (key: UtmKey, value: string) => void;
}

const ReferralContext = createContext<ContextValue | null>(null);

export function ReferralProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);

  const value = useMemo<ContextValue>(
    () => ({
      ...state,
      setPartnerId: (val) => dispatch({ type: "setPartnerId", value: val }),
      setChan: (val) => dispatch({ type: "setChan", value: val }),
      setB: (val) => dispatch({ type: "setB", value: val }),
      setEntryPoint: (val) => dispatch({ type: "setEntryPoint", value: val }),
      setCompanies: (codes, cleared) =>
        dispatch({ type: "setCompanies", value: codes, cleared }),
      setHideHeader: (val) => dispatch({ type: "setHideHeader", value: val }),
      setHideFooter: (val) => dispatch({ type: "setHideFooter", value: val }),
      setNoJivochat: (val) => dispatch({ type: "setNoJivochat", value: val }),
      setUtm: (key, val) => dispatch({ type: "setUtm", key, value: val }),
    }),
    [state],
  );

  return <ReferralContext.Provider value={value}>{children}</ReferralContext.Provider>;
}

export function useReferral(): ContextValue {
  const ctx = useContext(ReferralContext);
  if (!ctx) throw new Error("useReferral must be used within ReferralProvider");
  return ctx;
}
